import { Component, Input, Output, EventEmitter, TemplateRef, ContentChild, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  animations: [
    trigger('dialogAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.7)' }),
        animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        animate('75ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0, transform: 'scale(0.95)' }))
      ])
    ]),
    trigger('backdropAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('75ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class DialogComponent implements AfterViewInit {
  @Input() visible: boolean = false;
  @Input() header: string = '';
  @Input() width: string = '500px';
  @Input() height: string = 'auto';
  @Input() modal: boolean = true;
  @Input() closable: boolean = true;
  @Input() showHeader: boolean = true;
  @Input() showFooter: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() dismissableMask: boolean = false;
  @Input() draggable: boolean = true;
  @Input() position: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'topleft' | 'topright' | 'bottomleft' | 'bottomright' = 'center';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onShow = new EventEmitter<void>();
  @Output() onHide = new EventEmitter<void>();

  @ContentChild('header') headerTemplate!: TemplateRef<any>;
  @ContentChild('content') contentTemplate!: TemplateRef<any>;
  @ContentChild('footer') footerTemplate!: TemplateRef<any>;

  @ViewChild('dialog') dialogElement!: ElementRef;

  private isDragging = false;
  private dragOffset = { x: 0, y: 0 };

  ngAfterViewInit() {
    if (this.draggable && this.dialogElement) {
      this.setupDraggable();
    }
  }

  setupDraggable() {
    const dialogEl = this.dialogElement.nativeElement;
    const headerEl = dialogEl.querySelector('.dialog-header');

    if (!headerEl) return;

    headerEl.style.cursor = 'move';

    headerEl.addEventListener('mousedown', (e: MouseEvent) => {
      this.isDragging = true;
      const rect = dialogEl.getBoundingClientRect();
      this.dragOffset.x = e.clientX - rect.left;
      this.dragOffset.y = e.clientY - rect.top;

      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
    });
  }

  private handleMouseMove = (e: MouseEvent) => {
    if (!this.isDragging) return;

    const dialogEl = this.dialogElement.nativeElement;
    dialogEl.style.position = 'fixed';
    dialogEl.style.left = `${e.clientX - this.dragOffset.x}px`;
    dialogEl.style.top = `${e.clientY - this.dragOffset.y}px`;
  };

  private handleMouseUp = () => {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  };

  show() {
    this.visible = true;
    this.visibleChange.emit(true);
    this.onShow.emit();

    if (this.closeOnEscape) {
      document.addEventListener('keydown', this.handleEscape);
    }
  }

  hide() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.onHide.emit();

    if (this.closeOnEscape) {
      document.removeEventListener('keydown', this.handleEscape);
    }
  }

  private handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.closable) {
      this.hide();
    }
  };

  onMaskClick() {
    if (this.dismissableMask && this.closable) {
      this.hide();
    }
  }

  onDialogClick(event: MouseEvent) {
    event.stopPropagation();
  }
}
